import React from 'react';
import { observable, makeObservable, action } from 'mobx';
import MaskedInput from 'react-text-mask';
import 'tailwindcss/tailwind.css';
import emailMask from 'text-mask-addons/dist/emailMask';
import { memoize } from '../../utils/mobx/decorators/memoize';
import { Store } from '../../pagepart/store/Store';
import { BasicPortalStructure } from '../../pagepart/layout/BasicPortalStructure';
import { StoreContext } from 'src/pagepart/utils';
import { Env } from '../../env/Env';
import { ServerProps } from '../../pagepart/portal/utils';
import { helper_isValidEmail } from '../../utils/helper/helper_string';
import { prismaService } from '../../server/service/PrismaService';
import { genRandomHex } from '../../utils/helper/helper_crypt';
import { envService, userService } from '../../server';
import { mailService } from '../../server/service/MailService';
import { BasicAuthBox } from '../../pagepart/auth/BasicAuthBox';
import { hostService } from '../../server/service/HostService';
import { randomInteger } from '../../utils/helper/helper_math';
import { localStore } from '../../utils/mobx/localStorage/MobxLocalStorage';

export async function getServerSideProps(rawContext) {
  const { env, requestEnv } = await Env.forNextContext(rawContext);

  const { post, get } = requestEnv;

  return {
    props: {
      isEmailSyntaxInvalid: !!get.isEmailSyntaxInvalid,
      ...env.transfer,
      defaultEmail: get.email || '',
    },
  };
}

class AuthEmail extends React.Component<ServerProps<typeof getServerSideProps>> {
  @observable emailInputValue: boolean = false;

  constructor(props: ServerProps<typeof getServerSideProps>) {
    super(props);
    makeObservable(this);
  }

  @memoize
  get store() {
    return Store.forApp(this);
  }

  setupPage(store: Store) {
    store.portalStore.metaTitle = 'Anmelden mit E-Mail';
    store.portalStore.includeStylesheetOnce('/static/dist7/assets/css/pages/login/classic/login-4.css');
  }

  render() {
    const { isEmailSyntaxInvalid, defaultEmail } = this.props;

    return (
      <StoreContext.Provider value={this.store}>
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <img className="mx-auto h-12 w-auto" src="/images/schlaukopf_neu.svg" alt="Schlaukopf" />
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Anmelden mit E-Mail</h2>
            <p className="mt-2 text-center text-sm text-gray-600">Erhalte ein Einmal-Passwort per E-Mail.</p>
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <form className="space-y-6" action="/auth/passcode" method="POST">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    E-Mailadresse
                  </label>
                  <div className="mt-1">
                    <MaskedInput
                      autoFocus={true}
                      mask={emailMask}
                      defaultValue={defaultEmail || ''}
                      id="email"
                      name="email"
                      type="text"
                      autoComplete="email"
                      required
                      onChange={action(e => (this.emailInputValue = e.target.value))}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                    {isEmailSyntaxInvalid && <p className="text-danger">Gib eine gültige E-Mailadresse ein.</p>}
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    onClick={() => localStore.setItem('email', this.emailInputValue)}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Einmal-Passwort senden
                  </button>
                </div>
              </form>

              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">oder weiter mit</span>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3">
                  <div>
                    <a
                      href="/auth/signin"
                      className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span className="sr-only">Anmelden mit Google</span>
                      <img
                        className="w-5 h-5"
                        src="data:image/png;base64,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"
                      />
                    </a>
                  </div>

                  <div>
                    {' '}
                    <a
                      href="/auth/signin"
                      className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span className="sr-only">Anmelden mit Facebook</span>
                      <svg className="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </StoreContext.Provider>
    );
  }

  componentDidMount() {}
}

export default AuthEmail;
